import { useTranslation } from "react-i18next";

import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setGeoLocation } from "app/slices/globalSlice";
import { closeModal } from "app/slices/modalSlice";
import { GEO_OPTIONS, storeGeoLocation } from "GeoLocation";
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";

const PolicyEN = () => {
  return (
    <div className="h-fit text-justify flex  flex-col gap-2">
      <span>
        In compliance with official regulations, we need to access the location
        of the device you are using to operate on our digital platform. This
        allows us to provide enhanced security and meet applicable regulatory
        requirements.
      </span>
      <span className=" ">
        Your data will be handled with the confidentiality established in our{" "}
        <a
          href="https://crosspoint.global/privacy-policy"
          target="_blank"
          rel="noreferrer"
          className="underline text-primary"
        >
          Privacy Notice
        </a>
        .
      </span>
    </div>
  );
};

const PolicyES = () => {
  return (
    <div className="h-fit text-justify flex  flex-col gap-2 pb-2">
      <span>
        En cumplimiento de las disposiciones oficiales, necesitamos acceder a la
        ubicación del dispositivo desde el cual usted opera en nuestra
        plataforma digital. Esto nos permite ofrecerle mayor seguridad y cumplir
        con los requisitos regulatorios aplicables.
      </span>
      <span className=" ">
        Su información será tratada con la confidencialidad establecida en
        nuestro{" "}
        <a
          href="https://crosspoint.global/es/politica-de-privacidad"
          target="_blank"
          rel="noreferrer"
          className="underline text-primary"
        >
          Aviso de privacidad
        </a>
        .
      </span>
    </div>
  );
};

type PermissionState = "granted" | "prompt" | "denied" | "unknown";

// Where the failure actually lives — is it the browser blocking us, the
// OS/location service failing, or something else entirely?
type ErrorSource =
  | "unsupported" // browser has no geolocation API at all
  | "insecure-context" // page not served over HTTPS — API is disabled
  | "browser-permission" // user/browser denied the permission
  | "device" // permission OK, but OS/location service can't resolve a fix
  | "timeout" // permission OK, but no fix arrived in time
  | "unknown";

const ERROR_CODE_NAMES: Record<number, string> = {
  1: "PERMISSION_DENIED",
  2: "POSITION_UNAVAILABLE",
  3: "TIMEOUT",
};

const classifyError = (
  supported: boolean,
  secureContext: boolean,
  permission: PermissionState,
  error: GeolocationPositionError | null,
  wrapperError: string,
  wrapperErrorCode?: number,
): ErrorSource | null => {
  if (!supported) return "unsupported";
  if (!secureContext) return "insecure-context";
  const code = error?.code ?? wrapperErrorCode;
  if (code === 1) return "browser-permission";
  if (code === 2) return "device";
  if (code === 3) return "timeout";
  if (error) return "unknown";
  if (permission === "denied") return "browser-permission";
  if (wrapperError) {
    // Permission is granted, so whatever failed is on the device/OS side —
    // don't tell the user access is disabled.
    return permission === "granted" ? "device" : "unknown";
  }
  return null;
};

export const AcceptGeoLocation = ({
  data,
}: {
  data: {
    error: string;
    errorCode?: number;
    loading: boolean;
  };
}) => {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();

  const [
    { requesting, requestError, attempts, highAccuracyUsed },
    setRequestState,
  ] = useState<{
    requesting: boolean;
    requestError: GeolocationPositionError | null;
    attempts: number;
    highAccuracyUsed: boolean;
  }>({
    requesting: false,
    requestError: null,
    attempts: 0,
    highAccuracyUsed: false,
  });

  const supported = Boolean(navigator.geolocation);
  const secureContext = window.isSecureContext !== false;

  // Live browser permission state — tells us whether the browser itself is
  // blocking the request (denied), hasn't asked yet (prompt), or has already
  // granted it (in which case any failure is a device/OS problem, not a
  // permission problem).
  const [permission, setPermission] = useState<PermissionState>("unknown");
  useEffect(() => {
    let status: PermissionStatus | undefined;
    const onChange = () => {
      if (status) setPermission(status.state as PermissionState);
    };
    navigator.permissions
      ?.query({ name: "geolocation" })
      .then((s) => {
        status = s;
        setPermission(s.state as PermissionState);
        s.addEventListener("change", onChange);
      })
      .catch(() => setPermission("unknown"));
    return () => status?.removeEventListener("change", onChange);
  }, []);

  // Requesting the position from a click (user gesture) is the most reliable
  // path across browsers — Safari in particular can silently ignore
  // geolocation requests fired automatically on page load.
  const handleShareLocation = () => {
    setRequestState((s) => ({
      ...s,
      requesting: true,
      requestError: null,
      attempts: s.attempts + 1,
      highAccuracyUsed: false,
    }));

    const onSuccess = (a: GeolocationPosition) => {
      storeGeoLocation(String(a.coords.latitude), String(a.coords.longitude));
      dispatch(
        setGeoLocation({
          latitude: String(a.coords.latitude),
          longitude: String(a.coords.longitude),
        }),
      );
      dispatch(closeModal());
    };

    navigator.geolocation.getCurrentPosition(
      onSuccess,
      (e) => {
        if (e.code === e.PERMISSION_DENIED) {
          setRequestState((s) => ({
            ...s,
            requesting: false,
            requestError: e,
          }));
          return;
        }
        // POSITION_UNAVAILABLE / TIMEOUT: the low-power provider (network
        // location service) failed even though permission is granted — retry
        // once with high accuracy, which uses a different provider path
        // (GPS/WiFi) and often succeeds where the network service fails.
        setRequestState((s) => ({ ...s, highAccuracyUsed: true }));
        navigator.geolocation.getCurrentPosition(
          onSuccess,
          (e2) => {
            if (e2.code === e2.PERMISSION_DENIED) {
              setRequestState((s) => ({
                ...s,
                requesting: false,
                requestError: e2,
              }));
              return;
            }
            // Last resort: accept ANY cached fix, however old — a stale
            // position beats blocking the user on a flaky location service.
            navigator.geolocation.getCurrentPosition(
              onSuccess,
              () => {
                // Report the high-accuracy error; a cache miss isn't useful.
                setRequestState((s) => ({
                  ...s,
                  requesting: false,
                  requestError: e2,
                }));
              },
              {
                enableHighAccuracy: false,
                timeout: 5000,
                maximumAge: Infinity,
              },
            );
          },
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 },
        );
      },
      GEO_OPTIONS,
    );
  };

  const errorSource = classifyError(
    supported,
    secureContext,
    permission,
    requestError,
    data?.error || "",
    data?.errorCode,
  );

  const errorMessage = () => {
    switch (errorSource) {
      case "unsupported":
        return t(
          "Your browser does not support location services. Please use a different browser to continue",
        );
      case "insecure-context":
        return t(
          "Location is unavailable because this page is not loaded over a secure connection (HTTPS)",
        );
      case "browser-permission":
        return t(
          "Location access is currently disabled. Please enable it to continue",
        );
      case "device":
        return t(
          "Your device could not determine its location. Please check that Location Services are enabled in your system settings and try again",
        );
      case "timeout":
        return t(
          "Locating your device took too long. Please check your connection and try again",
        );
      case "unknown":
        return t(
          "Location access is currently disabled. Please enable it to continue",
        );
      default:
        return "";
    }
  };

  // Actionable, source-specific guidance so the user knows exactly WHERE to
  // fix the problem (browser vs operating system vs network).
  const guidance = () => {
    switch (errorSource) {
      case "browser-permission":
        return t(
          "Click the location icon in your browser's address bar, allow access for this site, then try again",
        );
      case "device":
        return t(
          "The browser has permission, but your operating system could not resolve a location. Enable Location Services in your system settings and check your internet connection",
        );
      case "timeout":
        return t(
          "The request timed out before a location was found. Move to an area with better connectivity or Wi-Fi coverage and try again",
        );
      default:
        return "";
    }
  };

  const error = errorMessage();
  const guidanceText = guidance();

  const permissionLabel: Record<PermissionState, string> = {
    granted: t("Granted"),
    prompt: t("Not yet requested"),
    denied: t("Denied"),
    unknown: t("Unknown"),
  };

  // Raw technical facts for debugging — permission state, error code/message,
  // context flags — so a screenshot from the user pinpoints the failure.
  const diagnostics: Array<{ label: string; value: string }> = [
    {
      label: t("Browser support"),
      value: supported ? t("Yes") : t("No"),
    },
    {
      label: t("Secure connection"),
      value: secureContext ? t("Yes") : t("No"),
    },
    {
      label: t("Browser permission"),
      value: permissionLabel[permission],
    },
    ...(requestError
      ? [
          {
            label: t("Error code"),
            value: `${requestError.code} (${
              ERROR_CODE_NAMES[requestError.code] || "UNKNOWN"
            })`,
          },
          {
            label: t("Error details"),
            value: requestError.message || "—",
          },
        ]
      : []),
    ...(data?.error && !requestError
      ? [
          ...(data.errorCode
            ? [
                {
                  label: t("Error code"),
                  value: `${data.errorCode} (${
                    ERROR_CODE_NAMES[data.errorCode] || "UNKNOWN"
                  })`,
                },
              ]
            : []),
          { label: t("Error details"), value: data.error },
        ]
      : []),
    ...(attempts > 0
      ? [
          {
            label: t("Attempts"),
            value: `${attempts}${
              highAccuracyUsed ? ` (${t("high accuracy retry used")})` : ""
            }`,
          },
        ]
      : []),
  ];

  const policy = () => {
    if (i18n.language.toLowerCase().startsWith("es")) {
      return PolicyES();
    }
    return PolicyEN();
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t("Geolocation")}</DialogTitle>
      </DialogHeader>

      <div className="flex flex-col gap-2">
        <span>
          {error || t("Crosspoint is requesting access to your location")}.
        </span>
        {guidanceText && (
          <span className="text-sm text-muted-foreground text-justify">
            {guidanceText}
          </span>
        )}
        {errorSource === "browser-permission" && (
          <img
            src="/images/geolocation.png"
            alt="How to enable location access in the browser"
            className="text-center justify-center align-middle self-center rounded-lg pt-2"
          />
        )}
        <Button
          className="w-full"
          onClick={handleShareLocation}
          disabled={requesting || !supported}
        >
          {requesting
            ? t("Requesting Location")
            : attempts > 0
              ? t("Try Again")
              : t("Share My Location")}
        </Button>
        {error && (
          <Collapsible>
            <CollapsibleContent>
              <div className="rounded-md border bg-muted/50 p-3 text-xs font-mono flex flex-col gap-1">
                {diagnostics.map(({ label, value }) => (
                  <div key={label} className="flex justify-between gap-4">
                    <span className="text-muted-foreground shrink-0">
                      {label}
                    </span>
                    <span className="text-right break-all">{value}</span>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
            <CollapsibleTrigger asChild>
              <Button className="w-full mt-2" variant="ghost" size="sm">
                {t("Technical Details")}
              </Button>
            </CollapsibleTrigger>
          </Collapsible>
        )}
        <Collapsible>
          <CollapsibleContent>
            <div className="flex flex-col gap-3 items-center">
              <div className="overflow-auto text-justify">{policy()}</div>
            </div>
          </CollapsibleContent>
          <CollapsibleTrigger asChild>
            <Button className="w-full pt-2" variant={"outline"}>
              {t("View More")}
            </Button>
          </CollapsibleTrigger>
        </Collapsible>
      </div>
    </DialogContent>
  );
};

export default AcceptGeoLocation;
